/**
 * IntentSelector — the compact "answer shape" chip on the research composer.
 *
 * `Auto` (the default) omits `intent` from the query so the engine classifies
 * it; anything else pins the answer to that `ResearchIntent`.
 */

import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { PiCaretDownLight, PiCheckLight } from 'react-icons/pi';
import clsx from 'clsx';
import { ResearchIntent } from './types';

/** `null` = Auto (let the engine classify). */
export type ComposerIntent = ResearchIntent | null;

interface IntentOption {
  value: ComposerIntent;
  /** Untranslated label — run through `t()` at render time. */
  label: string;
}

export const INTENT_OPTIONS: IntentOption[] = [
  { value: null, label: 'Auto' },
  { value: 'deep_research', label: 'Deep research' },
  { value: 'memo', label: 'Memo' },
  { value: 'draft', label: 'Draft document' },
  { value: 'chronology', label: 'Timeline' },
  { value: 'bullets', label: 'Bullets' },
  { value: 'find_document', label: 'Find document' },
  { value: 'overview', label: 'Overview' },
];

/** The composer label for an intent — also used for the "detected" badge. */
export function intentLabel(intent: ComposerIntent): string {
  const match = INTENT_OPTIONS.find((o) => o.value === intent);
  if (match) return match.label;
  // `qa` never appears in the picker; it is what Auto resolves to by default.
  return intent === 'qa' ? 'Answer' : 'Auto';
}

interface Props {
  value: ComposerIntent;
  onChange: (intent: ComposerIntent) => void;
  disabled?: boolean;
  className?: string;
}

const IntentSelector: React.FC<Props> = ({ value, onChange, disabled = false, className }) => {
  const { t } = useTranslation();

  return (
    <Menu as="div" className={clsx('relative inline-block text-left font-sans', className)}>
      <Menu.Button
        disabled={disabled}
        data-testid="intent-selector"
        className={clsx(
          'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
          'bg-surface-sunken text-fg-body border border-hairline-soft',
          'hover:border-champagne/50 transition-colors',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {value && <span className="w-1.5 h-1.5 rounded-full bg-champagne" aria-hidden />}
        {t(intentLabel(value))}
        <PiCaretDownLight className="w-3 h-3 text-fg-muted" aria-hidden />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-pop duration-150"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Menu.Items className="absolute bottom-full left-0 mb-1 w-44 origin-bottom-left z-30
                               glass rounded-lg shadow-sm p-1 focus:outline-none">
          {INTENT_OPTIONS.map((option) => (
            <Menu.Item key={option.label}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => onChange(option.value)}
                  className={clsx(
                    'flex items-center justify-between w-full px-2.5 py-1.5 rounded-md text-xs text-left',
                    active ? 'bg-surface-sunken text-fg-strong' : 'text-fg-body'
                  )}
                >
                  {t(option.label)}
                  {option.value === value && (
                    <PiCheckLight className="w-3.5 h-3.5 text-champagne" aria-hidden />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default IntentSelector;
